import type { FormEvent, JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useTargets } from '@/features/memorization/hooks';
import { RUBRIC_SCORE_LABELS } from '@/features/memorization/labels';
import type { RubricScore } from '@/features/memorization/types';
import { useCompleteSession } from '@/features/murajaah/hooks';
import { NEXT_REVIEW_RECOMMENDATION_LABELS } from '@/features/murajaah/labels';
import type {
  CompleteSessionReviewItemInput,
  MurajaahNextReviewRecommendation,
  MurajaahReviewItemView,
  MurajaahSessionView,
} from '@/features/murajaah/types';
import { ApiError } from '@/lib/api/client';

const TARGET_PICKER_LIMIT = 100;

interface DraftReviewItem {
  readonly targetId: string;
  readonly surah: string;
  readonly startAyah: string;
  readonly endAyah: string;
  readonly fluencyResult: RubricScore | '';
  readonly errorsNoted: string;
  readonly nextReviewRecommendation: MurajaahNextReviewRecommendation | '';
}

const RUBRIC_OPTIONS: readonly RubricScore[] = ['EXCELLENT', 'GOOD', 'FAIR', 'NEEDS_IMPROVEMENT'];
const RECOMMENDATION_OPTIONS: readonly MurajaahNextReviewRecommendation[] = [
  'REVIEW_AGAIN_SOON',
  'REVIEW_ON_SCHEDULE',
  'GRADUATE_TO_MAINTENANCE',
];

function emptyItem(): DraftReviewItem {
  return {
    targetId: '',
    surah: '',
    startAyah: '',
    endAyah: '',
    fluencyResult: '',
    errorsNoted: '',
    nextReviewRecommendation: '',
  };
}

/**
 * `POST /memorization/murajaah/:id/complete` (Phase G-2, Job 17/19). The
 * returned `reviewItems` are shown ONLY here, transiently, right after
 * completion - there is no GET endpoint to fetch them again later
 * (confirmed by the G-2 contract audit), so this dialog is the one and
 * only place a Teacher will ever see this session's recorded results.
 */
export function CompleteSessionDialog({
  studentId,
  session,
  open,
  onOpenChange,
}: {
  readonly studentId: string;
  readonly session: MurajaahSessionView;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const [items, setItems] = useState<readonly DraftReviewItem[]>([emptyItem()]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [result, setResult] = useState<readonly MurajaahReviewItemView[] | null>(null);
  const { data: targets } = useTargets(studentId, 1, TARGET_PICKER_LIMIT);
  const complete = useCompleteSession(studentId, session.id);

  const openTargets = targets?.items.filter((target) => target.status !== 'CANCELLED') ?? [];

  function reset(): void {
    setItems([emptyItem()]);
    setValidationError(null);
    setResult(null);
    complete.reset();
  }

  function handleOpenChange(next: boolean): void {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  }

  function updateItem(index: number, patch: Partial<DraftReviewItem>): void {
    setItems((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  function selectTarget(index: number, targetId: string): void {
    const target = openTargets.find((t) => t.id === targetId);
    updateItem(index, {
      targetId,
      surah: target !== undefined ? String(target.surah) : '',
      startAyah: target !== undefined ? String(target.startAyah) : '',
      endAyah: target !== undefined ? String(target.endAyah) : '',
    });
  }

  function addItem(): void {
    setItems((current) => [...current, emptyItem()]);
  }

  function removeItem(index: number): void {
    setItems((current) => current.filter((_, i) => i !== index));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);

    const parsed: CompleteSessionReviewItemInput[] = [];
    for (const item of items) {
      if (item.targetId === '') {
        setValidationError('Pilih target hafalan pada setiap item review.');
        return;
      }
      const surahNumber = Number(item.surah);
      const startAyahNumber = Number(item.startAyah);
      const endAyahNumber = Number(item.endAyah);
      if (!Number.isInteger(surahNumber) || surahNumber < 1) {
        setValidationError('Surah tidak valid pada salah satu item review.');
        return;
      }
      if (!Number.isInteger(startAyahNumber) || startAyahNumber < 1) {
        setValidationError('Ayat awal tidak valid pada salah satu item review.');
        return;
      }
      if (!Number.isInteger(endAyahNumber) || endAyahNumber < 1) {
        setValidationError('Ayat akhir tidak valid pada salah satu item review.');
        return;
      }
      if (endAyahNumber < startAyahNumber) {
        setValidationError('Ayat akhir tidak boleh lebih kecil dari ayat awal.');
        return;
      }
      if (item.fluencyResult === '') {
        setValidationError('Pilih hasil kelancaran pada setiap item review.');
        return;
      }
      if (item.nextReviewRecommendation === '') {
        setValidationError('Pilih rekomendasi review berikutnya pada setiap item review.');
        return;
      }
      parsed.push({
        targetId: item.targetId,
        surah: surahNumber,
        startAyah: startAyahNumber,
        endAyah: endAyahNumber,
        fluencyResult: item.fluencyResult,
        errorsNoted: item.errorsNoted.trim().length > 0 ? item.errorsNoted.trim() : null,
        nextReviewRecommendation: item.nextReviewRecommendation,
      });
    }

    complete.mutate(
      { reviewItems: parsed },
      {
        onSuccess: (data) => {
          setResult(data.reviewItems);
        },
      },
    );
  }

  const apiErrorMessage =
    complete.error instanceof ApiError
      ? complete.error.message
      : complete.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        {result !== null ? (
          <>
            <DialogHeader>
              <DialogTitle>Murajaah Selesai</DialogTitle>
              <DialogDescription>
                Hasil review ini hanya ditampilkan sekali di sini - tidak dapat dibuka kembali
                nanti.
              </DialogDescription>
            </DialogHeader>
            <ul className="flex flex-col gap-2">
              {result.map((item) => (
                <li
                  key={item.id}
                  className="flex flex-col gap-1 rounded-md border border-border p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">
                      Surah {item.surah}: Ayat {item.startAyah}
                      {item.endAyah !== item.startAyah ? `-${item.endAyah}` : ''}
                    </span>
                    <Badge>{RUBRIC_SCORE_LABELS[item.fluencyResult]}</Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {NEXT_REVIEW_RECOMMENDATION_LABELS[item.nextReviewRecommendation]}
                  </span>
                  {item.errorsNoted !== null && (
                    <span className="text-xs text-muted-foreground">
                      Catatan: {item.errorsNoted}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <DialogFooter>
              <Button
                type="button"
                onClick={() => {
                  handleOpenChange(false);
                }}
              >
                Tutup
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Selesaikan Murajaah</DialogTitle>
              <DialogDescription>Catat hasil review untuk setiap materi.</DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="flex flex-col gap-3 rounded-md border border-border p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">Item {index + 1}</span>
                    {items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          removeItem(index);
                        }}
                      >
                        Hapus
                      </Button>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <Label htmlFor={`review-${String(index)}-target`}>Target Hafalan</Label>
                    <Select
                      value={item.targetId}
                      onValueChange={(value) => {
                        selectTarget(index, value);
                      }}
                    >
                      <SelectTrigger id={`review-${String(index)}-target`}>
                        <SelectValue placeholder="Pilih target" />
                      </SelectTrigger>
                      <SelectContent>
                        {openTargets.map((target) => (
                          <SelectItem key={target.id} value={target.id}>
                            Surah {target.surah}: Ayat {target.startAyah}
                            {target.endAyah !== target.startAyah ? `-${target.endAyah}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <Label htmlFor={`review-${String(index)}-start`}>Ayat Awal</Label>
                      <Input
                        id={`review-${String(index)}-start`}
                        type="number"
                        min={1}
                        value={item.startAyah}
                        onChange={(event) => {
                          updateItem(index, { startAyah: event.target.value });
                        }}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label htmlFor={`review-${String(index)}-end`}>Ayat Akhir</Label>
                      <Input
                        id={`review-${String(index)}-end`}
                        type="number"
                        min={1}
                        value={item.endAyah}
                        onChange={(event) => {
                          updateItem(index, { endAyah: event.target.value });
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <Label htmlFor={`review-${String(index)}-fluency`}>Kelancaran</Label>
                    <Select
                      value={item.fluencyResult}
                      onValueChange={(value) => {
                        updateItem(index, { fluencyResult: value as RubricScore });
                      }}
                    >
                      <SelectTrigger id={`review-${String(index)}-fluency`}>
                        <SelectValue placeholder="Pilih" />
                      </SelectTrigger>
                      <SelectContent>
                        {RUBRIC_OPTIONS.map((score) => (
                          <SelectItem key={score} value={score}>
                            {RUBRIC_SCORE_LABELS[score]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-col gap-1">
                    <Label htmlFor={`review-${String(index)}-recommendation`}>
                      Rekomendasi Berikutnya
                    </Label>
                    <Select
                      value={item.nextReviewRecommendation}
                      onValueChange={(value) => {
                        updateItem(index, {
                          nextReviewRecommendation: value as MurajaahNextReviewRecommendation,
                        });
                      }}
                    >
                      <SelectTrigger id={`review-${String(index)}-recommendation`}>
                        <SelectValue placeholder="Pilih" />
                      </SelectTrigger>
                      <SelectContent>
                        {RECOMMENDATION_OPTIONS.map((recommendation) => (
                          <SelectItem key={recommendation} value={recommendation}>
                            {NEXT_REVIEW_RECOMMENDATION_LABELS[recommendation]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-col gap-1">
                    <Label htmlFor={`review-${String(index)}-errors`}>
                      Catatan Kesalahan (opsional)
                    </Label>
                    <Textarea
                      id={`review-${String(index)}-errors`}
                      value={item.errorsNoted}
                      onChange={(event) => {
                        updateItem(index, { errorsNoted: event.target.value });
                      }}
                    />
                  </div>
                </div>
              ))}

              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                Tambah Item
              </Button>

              {validationError !== null && (
                <Alert variant="destructive">
                  <AlertDescription>{validationError}</AlertDescription>
                </Alert>
              )}
              {apiErrorMessage !== null && (
                <Alert variant="destructive">
                  <AlertDescription>{apiErrorMessage}</AlertDescription>
                </Alert>
              )}

              <DialogFooter>
                <Button type="submit" disabled={complete.isPending}>
                  {complete.isPending ? 'Menyimpan...' : 'Selesaikan Murajaah'}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
